import React, { useState } from 'react';
import { X, Plus } from 'lucide-react'; // Plusアイコンを追加でインポート

export interface Pattern {
  patternId: string;
  cloudFrontUrl: string; // CloudFrontのURLを必須に変更
  createdAt: string;
}

interface PatternGalleryProps {
  patterns: Pattern[];
  onSelect: (svgUrl: string) => void;
  isLoading: boolean;
  onDelete?: (patternId: string) => Promise<boolean>; // 削除処理用コールバック
  onUpload?: (file: File) => Promise<boolean>; // アップロード処理用コールバックを追加
}

const PatternGallery: React.FC<PatternGalleryProps> = ({
  patterns,
  onSelect,
  isLoading,
  onDelete,
  onUpload,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [patternToDelete, setPatternToDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // アップロード関連の状態を追加
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // 著作権確認ダイアログの状態
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [copyrightConfirmed, setCopyrightConfirmed] = useState(false);

  // ファイル選択ダイアログを開く
  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // ファイル選択時の処理
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;

    // SVGファイルのみ許可
    if (file.type !== 'image/svg+xml' && !file.name.endsWith('.svg')) {
      setUploadError('SVGファイルのみアップロードできます');
      return;
    }

    // 選択されたファイルを保存し、確認ダイアログを表示
    setSelectedFile(file);
    setShowConfirmDialog(true);
  };

  // 著作権確認後のアップロード処理
  const handleConfirmedUpload = async () => {
    if (!selectedFile || !onUpload || !copyrightConfirmed) return;

    setIsUploading(true);
    setUploadError(null);
    setShowConfirmDialog(false);

    try {
      const success = await onUpload(selectedFile);
      if (!success) {
        setUploadError('アップロードに失敗しました');
      }
    } catch (error) {
      console.error('アップロードエラー:', error);
      setUploadError('アップロード中にエラーが発生しました');
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setCopyrightConfirmed(false);
      // ファイル入力をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-2 text-sm text-gray-500">読み込み中...</span>
      </div>
    );
  }

  // パターンがない場合でもアップロードボタンを表示
  if (patterns.length === 0) {
    return (
      <div>
        {/* エラー表示 */}
        {uploadError && (
          <div className="bg-red-100 text-red-700 p-2 mb-2 rounded text-xs">
            {uploadError}
          </div>
        )}

        <div className="grid grid-cols-3 gap-1 mt-2">
          {/* アップロードボタン */}
          {onUpload && (
            <div
              className="border border-dashed border-blue-300 rounded p-1 cursor-pointer transition-all duration-200 hover:border-blue-500 hover:bg-blue-50"
              onClick={openFileSelector}
            >
              <div className="bg-gray-50 flex flex-col items-center justify-center h-12 overflow-hidden">
                {isUploading ? (
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Plus size={16} className="text-blue-500" />
                    <span className="text-xs text-blue-500">追加</span>
                  </>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".svg"
                className="hidden"
                disabled={isUploading}
              />
            </div>
          )}
        </div>

        {/* 著作権確認ダイアログは保持 */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-md shadow-lg max-w-md w-full">
              <h3 className="text-lg font-medium mb-3">アップロード確認</h3>

              <p className="text-sm text-gray-600 mb-4">
                アップロードするファイル:{' '}
                <span className="font-medium">{selectedFile?.name}</span>
              </p>

              <div className="bg-yellow-50 border border-yellow-100 rounded p-3 mb-4">
                <p className="text-sm text-gray-700 mb-2">
                  以下の内容を確認してチェックを入れてください:
                </p>
                <div className="flex items-start mb-2">
                  <input
                    type="checkbox"
                    id="copyright-check"
                    checked={copyrightConfirmed}
                    onChange={(e) => setCopyrightConfirmed(e.target.checked)}
                    className="mt-1 mr-2"
                  />
                  <label
                    htmlFor="copyright-check"
                    className="text-xs text-gray-600"
                  >
                    このパターンは自分で作成したもの、または使用・アップロードする権利を持っていることを確認します。
                    第三者の著作権、商標権、その他の知的財産権を侵害するものではありません。
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowConfirmDialog(false);
                    setSelectedFile(null);
                    setCopyrightConfirmed(false);
                  }}
                  className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleConfirmedUpload}
                  disabled={!copyrightConfirmed}
                  className={`px-3 py-1.5 rounded text-sm text-white ${
                    copyrightConfirmed
                      ? 'bg-blue-500 hover:bg-blue-600'
                      : 'bg-blue-300 cursor-not-allowed'
                  }`}
                >
                  アップロード
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 削除ボタンのクリックハンドラー
  const handleDeleteClick = (e: React.MouseEvent, patternId: string) => {
    e.stopPropagation(); // 親要素へのクリックイベント伝播を防止
    setPatternToDelete(patternId);
  };

  // 削除確認処理
  const confirmDelete = async () => {
    if (!patternToDelete || !onDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const success = await onDelete(patternToDelete);
      if (success) {
        setPatternToDelete(null);
      } else {
        setDeleteError('削除に失敗しました');
      }
    } catch (error) {
      console.error('パターン削除エラー:', error);
      setDeleteError('削除処理中にエラーが発生しました');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      {/* エラー表示 */}
      {uploadError && (
        <div className="bg-red-100 text-red-700 p-2 mb-2 rounded text-xs">
          {uploadError}
        </div>
      )}

      <div className="grid grid-cols-3 gap-1 mt-2">
        {/* アップロードボタン - 常に1番目に表示 */}
        {onUpload && (
          <div
            className="border border-dashed border-blue-300 rounded p-1 cursor-pointer transition-all duration-200 hover:border-blue-500 hover:bg-blue-50"
            onClick={openFileSelector}
          >
            <div className="bg-gray-50 flex flex-col items-center justify-center h-12 overflow-hidden">
              {isUploading ? (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <Plus size={16} className="text-blue-500" />
                  <span className="text-xs text-blue-500">追加</span>
                </>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".svg"
              className="hidden"
              disabled={isUploading}
            />
          </div>
        )}

        {/* パターンアイテム */}
        {patterns.map((pattern) => (
          <div
            key={pattern.patternId}
            className={`border rounded p-1 cursor-pointer transition-all duration-200 relative ${
              hoveredId === pattern.patternId
                ? 'border-blue-500 shadow-sm'
                : 'border-gray-200'
            }`}
            onMouseEnter={() => setHoveredId(pattern.patternId)}
            onMouseLeave={() => setHoveredId(null)}
            onClick={() => onSelect(pattern.cloudFrontUrl)}
          >
            <div className="bg-gray-50 flex items-center justify-center h-12 overflow-hidden">
              <img
                src={pattern.cloudFrontUrl}
                alt="SVG Pattern"
                className="max-h-full max-w-full object-contain p-1"
              />
            </div>
            <div className="text-xs text-center mt-1 text-gray-500 truncate">
              {new Date(pattern.createdAt).toLocaleDateString()}
            </div>

            {/* 削除ボタン - ホバー時のみ表示 */}
            {onDelete && hoveredId === pattern.patternId && (
              <button
                className="absolute top-0 right-0 bg-red-100 hover:bg-red-200 rounded-full p-0.5 m-0.5 transform transition-transform duration-200 hover:scale-110"
                onClick={(e) => handleDeleteClick(e, pattern.patternId)}
                title="パターンを削除"
              >
                <X size={12} className="text-red-500" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* 削除確認ダイアログ */}
      {patternToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-md shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-medium mb-3">パターンの削除確認</h3>
            <p className="text-sm text-gray-600 mb-4">
              このパターンを削除してもよろしいですか？この操作は元に戻せません。
            </p>

            {deleteError && (
              <div className="bg-red-100 text-red-700 p-2 rounded mb-3 text-sm">
                {deleteError}
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setPatternToDelete(null)}
                className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                disabled={isDeleting}
              >
                キャンセル
              </button>
              <button
                onClick={confirmDelete}
                className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-sm flex items-center"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    削除中...
                  </>
                ) : (
                  '削除する'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 著作権確認ダイアログ */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-md shadow-lg max-w-md w-full">
            <h3 className="text-lg font-medium mb-3">アップロード確認</h3>

            <p className="text-sm text-gray-600 mb-4">
              アップロードするファイル:{' '}
              <span className="font-medium">{selectedFile?.name}</span>
            </p>

            <div className="bg-yellow-50 border border-yellow-100 rounded p-3 mb-4">
              <p className="text-sm text-gray-700 mb-2">
                以下の内容を確認してチェックを入れてください:
              </p>
              <div className="flex items-start mb-2">
                <input
                  type="checkbox"
                  id="copyright-check"
                  checked={copyrightConfirmed}
                  onChange={(e) => setCopyrightConfirmed(e.target.checked)}
                  className="mt-1 mr-2"
                />
                <label
                  htmlFor="copyright-check"
                  className="text-xs text-gray-600"
                >
                  このパターンは自分で作成したもの、または使用・アップロードする権利を持っていることを確認します。
                  第三者の著作権、商標権、その他の知的財産権を侵害するものではありません。
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setSelectedFile(null);
                  setCopyrightConfirmed(false);
                }}
                className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-sm"
              >
                キャンセル
              </button>
              <button
                onClick={handleConfirmedUpload}
                disabled={!copyrightConfirmed}
                className={`px-3 py-1.5 rounded text-sm text-white ${
                  copyrightConfirmed
                    ? 'bg-blue-500 hover:bg-blue-600'
                    : 'bg-blue-300 cursor-not-allowed'
                }`}
              >
                アップロード
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatternGallery;
